import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AppInjector } from '../../app-injector';
import { IntegrationItemServiceInf, StatusString } from '../interfaces/integrations';
import { Office365DialogComponent } from '../../modules/integrations-applications/components/integ-config-dialogs/office365-dialog/office365-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { AdminService } from 'src/app/core/services/admin.service';
import { Account } from '@mitel/cloudlink-sdk';
import { StatusType} from '../constants/statusType.enum';
import { SpinnerService } from './spinner.service';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { AccessPolicy, Statement, PolicyPostRequest, PolicyPutRequest } from '@mitel/cloudlink-sdk/admin';
import { AccountsService } from './accounts.service';
import { RBACAuthGuardService } from '../guards/rbac-auth-guard.service';
import { ActionType, CategoryId } from '../utils/rbac.util';

const Office365Statement: Statement =  {
	statementId: 'Office365Integration',
	action: [
		'*'
	],
	resource: [
		"https://mitel.io/auth/applications/features/office-365"
	],
	effect: 'Allow' as const
}

@Injectable({
	providedIn: 'root'
})

// this service will hide whatever implementation we eventually have for gettinng the list of integrations
// types. Right now it is static
export class IntegOffice365Service implements IntegrationItemServiceInf {

	private adminService: AdminService;
	private translateService: TranslateService;
	private spinnerService: SpinnerService;
	public dialog: MatDialog;
	private accountsService: AccountsService;
	private rbacAuthGuardService: RBACAuthGuardService;

	constructor(  ) 
	{
	}

	initServices()
	{
		if ( !this.adminService )
			this.adminService = AppInjector.injector.get(AdminService);

		if ( !this.translateService )
			this.translateService = AppInjector.injector.get(TranslateService);
		
		if ( !this.spinnerService )
			this.spinnerService = AppInjector.injector.get(SpinnerService);

		if ( !this.accountsService )
			this.accountsService = AppInjector.injector.get(AccountsService);

		if ( !this.dialog )
			this.dialog = AppInjector.injector.get(MatDialog);

		if (!this.rbacAuthGuardService)
			this.rbacAuthGuardService = AppInjector.injector.get(RBACAuthGuardService);
	}
	
	getConfigurationDialog() : any
	{
		return Office365DialogComponent;
	}

	isSetupComplete(account: Account) : Promise<boolean>
	{
		this.initServices();
		return this.adminService.getAccountTags( account.accountId ).then( tags => {
			if ( tags && tags.Office365 )
			{
				if ( tags.Office365.consent == true || tags.Office365.enabled == true )
					return true;
				return false;
			}
			else
				return false;
		})
		.catch(err => {
			return false;
		})
	}

	handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any> {
		// add the policy on enable, show dialog and remove the policy on remove
		this.initServices();
		if ( state === false ) {
			// remove the policy
			return this.removeOffice365Policy(account).then( ()=> {
				// remove the tag
				return this.adminService.deleteAccountTag( account.accountId, 'Office365' ).then( data => {
					return;
				})
				.catch(err => {
					// an error is thrown if no tag exists. That is OK!
					return;
				})
			})
		}
		else {
			// this is a tri state integration
			
			return this.adminService.getAccountTags( account.accountId ).then( tags => {
				// there should be tags -- there would not be an integration without tags
				if ( tags && tags.Office365 && tags.Office365.enabled == false ) {
					// if there are Office365 tags and the enabled tags is not enabled, then do the following:
					// save that the integration is enabled
					console.log("Save the changes to the integration - when successful, close the dialog");
					//here we will save the policy for the integration
					//also save the enabled state.
			
					//policy
					return this.addOffice365Policy( account ).then( policy => {
						//enabled state
					
						let consent = false;
						let time = undefined;
						if ( tags && tags.Office365 ) {
							consent = tags.Office365.consent; //preserve the state of consent
							time = tags.Office365.verifiedDate;
						}
			
						let dataChanges = {
							policyId: policy.policyId,
							tags: {
								Office365: {
									enabled: true,
									consent: consent,
									verifiedDate: time
								}
							}
						}
						console.log("about to save the integration changes", dataChanges);
			
						return this.adminService.updateAccount( account.accountId, dataChanges).then(result => {
							this.accountsService.mergeAccountWithModel(result);
							this.spinnerService.hideSpinner(); //bizarre that this needs to be done as the service does not do it
						});
					});
				}
				else if ( tags && tags.Office365 == undefined ) {
					// if there are no Office365 tags, then it has never been added, so it is in the list but not enabled
					// this is OK... the integration was just added... set the enabled state to false

					let dataChanges = {
						tags: {
							Office365: {
								enabled: false,
								consent: undefined,
								verifiedDate: undefined
							}
						}
					}
					console.log("about to save the integration changes", dataChanges);
		
					return this.adminService.updateAccount( account.accountId, dataChanges).then(result => {
						this.accountsService.mergeAccountWithModel(result);
						this.spinnerService.hideSpinner(); //bizarre that this needs to be done as the service does not do it
					});
				}
			});
				
		}	
	}

	removeOffice365Policy( account: Account ): Promise<any>
	{
		// due to a test where admins will wait and mutiple admins could be changing the state of an account
		// we will request the account again in this case this is a real corner case
		return this.adminService.getAccount(account.accountId). then( newAccount => {
			if ( newAccount['policyId'] != undefined )
			{
				return this.adminService.getPolicy(newAccount.accountId, newAccount['policyId']).then( policy => {
					console.log("Here are the account policies", policy);
					// now find the statment and remove it
					const foundPolicy = policy.statements.find(stmt => {
						return stmt.statementId === "Office365Integration";
					});
					if ( foundPolicy )
					{
						//remove the statement
						console.log("Changing the policy to this", policy);
						return this.adminService.removeStatementFromPolicy(
							account.accountId, policy.policyId, foundPolicy.statementId).then(data => {
								console.log("this is the returned data", data)
								return data;
							})
					}
					else
						return policy;
				}) 
				.catch(err => {
					console.log("Here is the error from getting policies", err)
					return null;
				});
			}
			else
				return null;
		})
	}

	addOffice365Policy( account: Account ): Promise<AccessPolicy>
	{
		if ( account['policyId'] != undefined )
		{

			return this.adminService.getPolicy(account.accountId, account['policyId']).then( policy => {
				console.log("Here are the account policies", policy);
				const foundPolicy = policy.statements.find(stmt => {
					return stmt.statementId === "Office365Integration";
				});
				if (foundPolicy) {
					console.log("We found the policy exists")
					return policy;
				}
				else
				{
					return this.updatePolicyWithOffice365Statement( account.accountId, policy);
				}
			}) 
			.catch(err => {
				console.log("Here is the error from getting policies", err)
				return null;
			});
		}
		else
			return this.createOffice365Policy( account.accountId);
	}

	createOffice365Policy(accountId: string): Promise<AccessPolicy>
	{
        const policy: PolicyPostRequest = {
            name: accountId + '-policy',
            statements: [
                Office365Statement
            ]
        };
		return this.adminService.createUserPolicy(policy, accountId);
	}

	updatePolicyWithOffice365Statement( accountId: string, policy: AccessPolicy)
	{
		return this.adminService.updatePolicy( accountId, policy.policyId, null, [Office365Statement] );
	}

	getStatusString(account: Account): Promise<StatusString>
	{
		this.initServices();
		let status: StatusString = {
			status: undefined,
			type: StatusType.normal
		}

		return this.adminService.getAccountTags( account.accountId ).then( tags => {
			console.log("Here are the tags", tags)
			if ( tags && tags.Office365 &&  tags.Office365.consent == true )
			{
				// display the verified date
				var d = new Date(tags.Office365.verifiedDate);

				let verifiedDate = d.toLocaleDateString();
				let verifiedTime = d.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', timeZoneName: 'short'});
				status.status = this.translateService.instant('integrations.office365.last-verified', {date: verifiedDate, time: verifiedTime});
				return status;
			}
			else
			{
				status.status = 'integrations.office365.status-unknown'
				status.type = StatusType.caution;
				return status;
			}
		})
		.catch(err => {
			console.log("Could not get the tags. This should not happen", err);
			status.status = 'integrations.office365.status-unknown'
			status.type = StatusType.caution;
			return status;
		})

	}

	getConfigButtonLabel(account: Account): Promise<string>
	{
		return Promise.resolve('integrations.office365.setup-button');
	}

	isButtonDisabled(account: Account): Promise<boolean>
	{
		return Promise.resolve(false);
	}

	canDeleteIntegration(account: Account): Promise<boolean>
	{
		// must confirm removal
		return this.isSetupComplete(account).then(isSetup => {
			if ( isSetup === false )
			{
				return true; //there was nothing setup, so delete does not need to be confirmed
			}
			//display a dialog to so the user can confirm the deletion
			const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.REMOVE_OFFICE365_CONFIRMATION } });
			return dialogRef.afterClosed().toPromise().then(result => {
				return result;
			});
		});
	}

	isIntegrationStateOn(account: Account): Promise<boolean>
	{
		this.initServices();
		return this.adminService.getAccountTags( account.accountId ).then( tags => {
			console.log("Here are the tags", tags)
			if ( tags && tags.Office365 && tags.Office365.enabled != undefined )
				return  tags.Office365.enabled;
			else
				return false;
		})
		.catch(err => {
			return false;
		})
	}
	
	canBeSelected(account: Account): Promise<boolean>{
		this.initServices();

		let isDenied = this.rbacAuthGuardService.isPermissionDeniedForUser(CategoryId.INTEGRATIONOFFICE365, ActionType.VIEW);
		return Promise.resolve(!isDenied);
	}
}
